import { useContext, useRef } from "react";
import classNames from "classnames";

import { MapContext } from "../../../../Map";

import styles from "./PinyinSuggestions.module.scss";

const MAX_CANDIDATES = 9;

interface Props {
  candidates: string[];
  onCandidateSelect: (candidate: string) => void;
  dataQA?: string;
}

const PinyinSuggestions: React.FC<Props> = ({
  candidates,
  onCandidateSelect,
  dataQA = "pinyin-suggestions",
}) => {
  const { controlTheme } = useContext(MapContext);

  const containerRef = useRef<HTMLDivElement>(null);
  // To track if the user is currently dragging
  const isDragging = useRef(false);
  // Initial X coordinate when drag starts.
  const startX = useRef(0);
  // Initial scrollLeft of the container when drag starts.
  const scrollLeft = useRef(0);
  // To differentiate between click and drag so we don't trigger a click event on drag
  const dragged = useRef(false);

  // Get pageX from mouse or touch event
  const getPageX = (e: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in e) {
      return e.touches[0]?.pageX ?? 0;
    }
    return e.pageX;
  };

  // Start drag (mouse down / touch start)
  const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    dragged.current = false;
    // Record the initial position
    startX.current = getPageX(e) - (containerRef.current?.offsetLeft || 0);
    // Record the initial scroll position
    scrollLeft.current = containerRef.current?.scrollLeft || 0;
  };

  // Move drag (mouse move / touch move)
  const onDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return;
    if ("preventDefault" in e) e.preventDefault(); // block text selection / native scroll
    dragged.current = true;
    // Calculate the distance moved
    const x = getPageX(e) - (containerRef.current?.offsetLeft || 0);
    const walk = x - startX.current;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft.current - walk;
    }
  };

  // End drag (mouse up / leave / touch end / cancel)
  const onDragEnd = () => {
    isDragging.current = false;
  };

  // Handle candidate click, skip if it was actually a drag
  const onPillClick = (
    candidate: string,
    e: React.MouseEvent | React.TouchEvent,
  ) => {
    if (dragged.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onCandidateSelect(candidate);
  };

  return (
    <div
      data-qa={dataQA}
      ref={containerRef}
      // Mouse
      onMouseDown={onDragStart}
      onMouseMove={onDragMove}
      onMouseUp={onDragEnd}
      onMouseLeave={onDragEnd}
      // Touch
      onTouchStart={onDragStart}
      onTouchMove={onDragMove}
      onTouchEnd={onDragEnd}
      onTouchCancel={onDragEnd}
      className={classNames(styles.container, styles[controlTheme], {
        [styles.centerAlign]: candidates.length <= MAX_CANDIDATES,
      })}
    >
      <div className={styles.row}>
        {candidates.length > 0 &&
          candidates.map((candidate, index) => (
            <button
              key={`${candidate}-${index}`}
              onClick={(event) => onPillClick(candidate, event)}
              className={styles.pill}
            >
              {candidate}
            </button>
          ))}
      </div>
    </div>
  );
};

export default PinyinSuggestions;
